import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Input, ModalBody, ModalFooter } from 'reactstrap';
import {
  GOOGLE_PRIVACY_URL,
  YOUTUBE_MADE_FOR_KIDS_URL,
  YOUTUBE_TERMS_URL,
} from 'fym-shared/src/music24/youtubePostSettings';
import {
  YOUTUBE_DESCRIPTION_MAX_LENGTH,
  YOUTUBE_TITLE_MAX_LENGTH,
  defaultPublishTiming,
  type AssetPublishDefaultsResponse,
} from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useCreateAssetPublishMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { youtubeMessages } from '../composer/youtube/youtubeMessages';
import { publishMessages } from './publishMessages';
import { ONE_OFF_CHANNEL_NAMES, publishErrorText, publishTimeText } from './publishBadgeText';
import { localReleaseStart, publishAtOfValue, PublishTimingField, PublishTimingValue } from './PublishTimingField';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';
import { PublishChannelRow } from './PublishChannelRow';
import { PublishReplaceChoice } from './PublishReplaceChoice';
import { withBrandWord } from '../../components/primitives/BrandWord';

interface PublishFormProps {
  accountId: number;
  defaults: AssetPublishDefaultsResponse;
  onDone: () => void;
}

function link(href: string) {
  return (parts: React.ReactNode[]) => (
    <a href={href} target="_blank" rel="noreferrer">
      {parts}
    </a>
  );
}

/**
 * The "Publish to YouTube?" form (an API channel with no live record): the channel, the title and
 * the description of the release, when it goes public, and for YouTube the "made for kids" answer
 * (no default, YouTube terms) and the consent line. One click uploads.
 */
const PublishFormComponent: React.FC<PublishFormProps> = ({ accountId, defaults, onDone }) => {
  const intl = useIntl();
  const { channel } = defaults;
  const name = ONE_OFF_CHANNEL_NAMES[channel];
  const timeZone = useArtistTimeZone();
  const [title, setTitle] = useState(defaults.title);
  const [description, setDescription] = useState(defaults.description);
  const [timing, setTiming] = useState<PublishTimingValue>(() => ({
    timing: defaultPublishTiming(channel, localReleaseStart(defaults.releaseDate, timeZone), new Date()),
    pickedAt: '',
  }));
  const [madeForKids, setMadeForKids] = useState<boolean | null>(null);
  // "Replace the video on YouTube?" (owner decision 2026-10-09): replace is the default.
  const [replace, setReplace] = useState(true);
  const isInstagram = channel === 'instagram';
  const [create, { isLoading, error }] = useCreateAssetPublishMutation();
  const isYouTube = channel === 'youtube';
  const publishAt = publishAtOfValue(timing, defaults.releaseDate, timeZone);
  const canSubmit =
    !isLoading &&
    (!isYouTube || madeForKids !== null) &&
    (timing.timing !== 'later' || publishAt !== null) &&
    (isInstagram || title.trim() !== '');

  const submit = useCallback(async () => {
    const result = await create({
      accountId,
      body: {
        kitAssetUuid: defaults.kitAssetUuid,
        channel,
        timing: timing.timing,
        publishAt,
        title: title.trim(),
        description,
        ...(isYouTube && madeForKids !== null ? { madeForKids } : {}),
        ...(defaults.otherLive === null
          ? {}
          : replace
            ? { replacesPublishUuid: defaults.otherLive.uuid }
            : { keepBoth: true }),
      },
    });
    if (isSuccessfulResult(result)) {
      onDone();
    }
  }, [
    accountId,
    channel,
    create,
    defaults.kitAssetUuid,
    defaults.otherLive,
    description,
    isYouTube,
    madeForKids,
    onDone,
    publishAt,
    replace,
    timing.timing,
    title,
  ]);

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3">
        <PublishChannelRow connection={defaults.connection} channel={channel} />
        {defaults.otherLive !== null && (
          <PublishReplaceChoice live={defaults.otherLive} replace={replace} onChange={setReplace} />
        )}
        {!isInstagram && (
          <div>
            <label className="form-label fw-semibold" htmlFor="publish-title">
              {intl.formatMessage(publishMessages.promptVideoTitle)}
            </label>
            <Input
              id="publish-title"
              value={title}
              maxLength={YOUTUBE_TITLE_MAX_LENGTH}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
        )}
        <div>
          <label className="form-label fw-semibold" htmlFor="publish-description">
            {intl.formatMessage(isInstagram ? publishMessages.promptCaption : publishMessages.promptDescription)}
          </label>
          <Input
            id="publish-description"
            type="textarea"
            rows={4}
            value={description}
            maxLength={YOUTUBE_DESCRIPTION_MAX_LENGTH}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        {/* Instagram has no scheduled publish: no time choice, only "Post now as a Reel". */}
        {!isInstagram && (
          <PublishTimingField
            channel={channel}
            releaseDate={defaults.releaseDate}
            value={timing}
            onChange={setTiming}
            name="publish-when"
          />
        )}
        <small className="text-muted" data-testid="publish-when-line">
          {isInstagram
            ? intl.formatMessage(publishMessages.reelNow)
            : publishAt === null
              ? intl.formatMessage(publishMessages.publicNow)
              : intl.formatMessage(publishMessages.privateUntil, {
                  channel: name,
                  time: publishTimeText(intl, publishAt),
                })}
        </small>
        {isYouTube && (
          <fieldset className="d-flex flex-column gap-2">
            <legend className="fs-6 fw-semibold mb-1">{intl.formatMessage(youtubeMessages.kidsLabel)}</legend>
            {[true, false].map((answer) => (
              <div className="form-check" key={String(answer)}>
                <Input
                  id={`publish-kids-${String(answer)}`}
                  type="radio"
                  name="publish-kids"
                  className="form-check-input"
                  checked={madeForKids === answer}
                  onChange={() => setMadeForKids(answer)}
                />
                <label className="form-check-label" htmlFor={`publish-kids-${String(answer)}`}>
                  {intl.formatMessage(answer ? youtubeMessages.kidsYes : youtubeMessages.kidsNo)}
                </label>
              </div>
            ))}
            <small className="text-muted">
              {withBrandWord(intl.formatMessage(youtubeMessages.kidsNote))}{' '}
              <a href={YOUTUBE_MADE_FOR_KIDS_URL} target="_blank" rel="noreferrer">
                {intl.formatMessage(youtubeMessages.kidsLearnMore)}
              </a>
            </small>
          </fieldset>
        )}
        {isYouTube && <small className="text-muted">{intl.formatMessage(publishMessages.smartLinkNote)}</small>}
        {isYouTube && (
          <small className="text-muted" data-testid="publish-consent">
            {withBrandWord(
              intl.formatMessage(youtubeMessages.consent, {
                terms: link(YOUTUBE_TERMS_URL),
                privacy: link(GOOGLE_PRIVACY_URL),
              })
            )}
          </small>
        )}
        {error !== undefined && (
          <Alert color="danger" className="mb-0" data-testid="publish-error">
            {publishErrorText(intl, channel, error)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <SMLButton outline omitCap onClick={onDone}>
          {intl.formatMessage(publishMessages.notNow)}
        </SMLButton>
        <SMLButton color="primary" omitCap disabled={!canSubmit} onClick={() => submit().catch(console.error)}>
          {intl.formatMessage(isInstagram ? publishMessages.submitReel : publishMessages.submit)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const PublishForm = TypedMemo(PublishFormComponent);
